/**
 * LATEDAYS -- the `/time?date=YYYY-MM-DD&kind=Actual|Plan` deep link a
 * rejected-day notice opens. Pure, so vitest can pin it without a DOM.
 *
 * Each half is validated on its own and comes back `null` when absent or
 * invalid; the page keeps its own default (today, Actual) for a null half.
 */
import { addDays, iso, parseIso } from './dates'

type DayLinkKind = 'Actual' | 'Plan'

interface DayLink {
  date: string | null
  kind: DayLinkKind | null
}

/** A real calendar date in 'YYYY-MM-DD' form -- `2026-02-30` is refused, not rolled into March. */
export function validIsoDate(value: string | null | undefined): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  return iso(parseIso(value)) === value ? value : null
}

export function dayLinkFromSearch(params: URLSearchParams): DayLink {
  const kind = params.get('kind')
  return {
    date: validIsoDate(params.get('date')),
    kind: kind === 'Actual' || kind === 'Plan' ? kind : null,
  }
}

/** The link's own keys, so the page can drop them once applied and leave `?scope=` alone. */
export const DAY_LINK_KEYS = ['date', 'kind'] as const

export function timeDayHref(date: string | null | undefined, kind: string | null | undefined): string {
  const day = validIsoDate(date)
  if (!day) return '/time'
  return `/time?date=${day}&kind=${kind === 'Plan' ? 'Plan' : 'Actual'}`
}

export type DayStatusFilter = 'Pending' | 'Rejected'

const DAY_STATUS_FILTERS: readonly DayStatusFilter[] = ['Pending', 'Rejected']

export const DAY_STATUS_KEY = 'status'

/** `?status=Pending,Rejected`; unknown members are dropped and nothing valid reads as no filter. */
export function dayStatusFilterFromSearch(params: URLSearchParams): DayStatusFilter[] | null {
  const raw = params.get(DAY_STATUS_KEY)
  if (!raw) return null
  const wanted = new Set(raw.split(','))
  const picked = DAY_STATUS_FILTERS.filter((s) => wanted.has(s))
  return picked.length > 0 ? picked : null
}

export function timeStatusHref(statuses: readonly DayStatusFilter[]): string {
  const picked = DAY_STATUS_FILTERS.filter((s) => statuses.includes(s))
  return picked.length > 0 ? `/time?${DAY_STATUS_KEY}=${picked.join(',')}` : '/time'
}

/** The dashboard counts Pending and Rejected Actual days over this many days back, today included. */
const STATUS_WINDOW_DAYS = 60

export function statusWindow(today: string): { from: string; to: string } {
  return { from: addDays(today, -STATUS_WINDOW_DAYS), to: today }
}

/** The dates the status chip counted, newest first. */
export function daysInStatusWindow<D extends { day: string; kind: string; status: string }>(
  days: readonly D[],
  filter: readonly DayStatusFilter[],
  today: string,
): D[] {
  const { from, to } = statusWindow(today)
  return days
    .filter((d) => d.kind === 'Actual' && d.day >= from && d.day <= to && filter.some((s) => s === d.status))
    .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
}
